import React, {PropTypes, Component} from 'react';
import {autobind} from 'core-decorators';
import {Section, Field, Toggle, Checkbox, Select, TextInput} from 'components/forms';
import * as options from './options';

@autobind
export default class Form extends Component {
    static propTypes = {
        values: PropTypes.object.isRequired,
        mobile: PropTypes.bool.isRequired,
        onChange: PropTypes.func.isRequired,
        advancedView: PropTypes.bool.isRequired,
    };

    getFieldProps(key, overrides = {}) {
        return {
            value: this.props.values[key],
            disabled: this.props.values.onlySeo,
            onChange: value => this.props.onChange({[key]: value}),
            ...overrides,
        };
    }

    handleDomContainerEnabledChange(enable) {
        return () => {
            this.props.onChange({
                domContainerEnabled: enable,
                expandHeight: enable ? false : this.props.values.expandHeight,
            });
        };
    }

    handleIncludeSeoChange(includeSeo) {
        this.props.onChange({
            includeSeo,
            onlySeo: !includeSeo ? false : this.props.values.onlySeo,
        });
    }

    handleOnlySeoChange(onlySeo) {
        this.props.onChange({
            onlySeo,
            includeSeo: onlySeo ? true : this.props.values.includeSeo,
        });
    }

    render() {
        const {mobile, values, advancedView} = this.props;
        return (
            <div>
                <Section title='General'>
                    {advancedView && (
                        <Field label='Language' disabled={values.onlySeo}>
                            <Select
                                options={options.languageOptions}
                                {...this.getFieldProps('language')}
                            />
                        </Field>
                    )}
                    <Field label='Theme' disabled={values.onlySeo}>
                        <Select
                            options={options.themeOptions}
                            {...this.getFieldProps('theme')}
                        />
                    </Field>
                </Section>
                {advancedView && (
                    <Section title='SEO'>
                        <Field label='Include SEO'>
                            <Toggle
                                {...this.getFieldProps('includeSeo', {
                                    disabled: false,
                                    onChange: this.handleIncludeSeoChange,
                                })}
                            />
                        </Field>
                        <Field
                            label='Only SEO'
                            helpText='Include only public metadata, do not display wrapper or transcript information.'
                        >
                            <Toggle
                                {...this.getFieldProps('onlySeo', {
                                    disabled: false,
                                    onChange: this.handleOnlySeoChange,
                                })}
                            />
                        </Field>
                    </Section>
                )}
                {(!mobile || advancedView) && (
                    <Section title='Layout'>
                        {!mobile && (
                            <Field label='Video size'>
                                <Select
                                    options={options.videoSizeOptions}
                                    {...this.getFieldProps('videoSize')}
                                />
                            </Field>
                        )}
                        {advancedView && (
                            <Field disabled={values.onlySeo}>
                                <Checkbox
                                    label='Wrap video'
                                    value={!values.domContainerEnabled}
                                    disabled={values.onlySeo}
                                    onChange={this.handleDomContainerEnabledChange(false)}
                                />
                            </Field>
                        )}
                        {advancedView && (
                            <Field disabled={values.onlySeo}>
                                <Checkbox
                                    label='Use container'
                                    disabled={values.onlySeo}
                                    value={values.domContainerEnabled}
                                    onChange={this.handleDomContainerEnabledChange(true)}
                                />
                                <TextInput
                                    grow={true}
                                    placeholder='CSS selector'
                                    {...this.getFieldProps('domContainer', {
                                        disabled: !values.domContainerEnabled || values.onlySeo || mobile,
                                    })}
                                />
                            </Field>
                        )}
                    </Section>
                )}
                <Section title='Components'>
                    <Field label='Enable transcript' disabled={values.onlySeo}>
                        <Toggle {...this.getFieldProps('transcriptEnabled')} />
                    </Field>
                    <Field label='Enable captions' disabled={values.onlySeo}>
                        <Toggle {...this.getFieldProps('captionsEnabled')} />
                    </Field>
                    {advancedView && (
                        <Field label='Enable search' disabled={values.onlySeo}>
                            <Toggle {...this.getFieldProps('searchEnabled')} />
                        </Field>
                    )}
                    {advancedView && (
                        <Field label='Enable heatmap' disabled={values.onlySeo}>
                            <Toggle {...this.getFieldProps('heatmapEnabled')} />
                        </Field>
                    )}
                    <Field label='Enable print' icon='print' disabled={values.onlySeo}>
                        <Toggle {...this.getFieldProps('printEnabled')} />
                    </Field>
                    <Field label='Enable download' icon='download' disabled={values.onlySeo}>
                        <Toggle {...this.getFieldProps('downloadEnabled')} />
                    </Field>
                    <Field label='Enable share' icon='share' disabled={values.onlySeo}>
                        <Toggle {...this.getFieldProps('shareEnabled')} />
                    </Field>
                    <Field
                        label='Transcript Initially visible'
                        disabled={values.onlySeo || !values.transcriptEnabled}
                    >
                        <Toggle
                            {...this.getFieldProps('transcriptVisible', {
                                disabled: values.onlySeo || !values.transcriptEnabled,
                            })}
                        />
                    </Field>
                    <Field
                        label='Heatmap Initially visible'
                        disabled={values.onlySeo || !values.heatmapEnabled}
                    >
                        <Toggle
                            {...this.getFieldProps('heatmapVisible', {
                                disabled: values.onlySeo || !values.heatmapEnabled,
                            })}
                        />
                    </Field>
                    <Field label='Captions Initially visible' disabled={values.onlySeo || !values.captionsEnabled}>
                        <Toggle
                            {...this.getFieldProps('captionsVisible', {
                                disabled: values.onlySeo || !values.captionsEnabled,
                            })}
                        />
                    </Field>
                    <Field
                        label='Speakers initially visible'
                        disabled={values.onlySeo || !values.transcriptEnabled}
                    >
                        <Toggle
                            {...this.getFieldProps('speakersVisible', {
                                disabled: values.onlySeo || !values.transcriptEnabled,
                            })}
                        />
                    </Field>
                </Section>
                {advancedView && (
                    <Section title='Font sizes'>
                        <Field label='Transcript font-size'>
                            <TextInput grow={true} {...this.getFieldProps('transcriptFontSize')} />
                        </Field>
                        <Field label='Captions font-size'>
                            <TextInput grow={true} {...this.getFieldProps('captionsFontSize')} />
                        </Field>
                    </Section>
                )}
                <Section title='Search'>
                    <Field label='Search position' disabled={values.onlySeo}>
                        <Select
                            options={options.searchPositionOptions}
                            {...this.getFieldProps('searchPosition')}
                        />
                    </Field>
                    {advancedView && (
                        <Field label='Initial Search'>
                            <TextInput
                                grow={true}
                                placeholder='Initial search query'
                                {...this.getFieldProps('searchQuery', {disabled: false})}
                            />
                        </Field>
                    )}
                    {advancedView && (
                        <Field
                            label='Search Variable'
                            helpText='Variable to be used to extract search query from URL querystring.'
                        >
                            <TextInput
                                grow={true}
                                placeholder='Search variable'
                                {...this.getFieldProps('searchVariable', {disabled: false})}
                            />
                        </Field>
                    )}
                </Section>
                <Section title='Karaoke highlighting'>
                    <Field
                        label='Enabled'
                        helpText='Highlight the current word being spoken, like song lyrics during Karaoke.'
                        disabled={values.onlySeo || !values.transcriptEnabled}
                    >
                        <Toggle
                            {...this.getFieldProps('wordHighlight', {
                                disabled: values.onlySeo || !values.transcriptEnabled,
                            })}
                        />
                    </Field>
                    <Field
                        label='User configurable'
                        helpText='Allow users to toggle "Karaoke highlighting" from the settings menu.'
                        disabled={values.onlySeo || !values.transcriptEnabled}
                    >
                        <Toggle
                            {...this.getFieldProps('wordHighlightOption', {
                                disabled: values.onlySeo || !values.transcriptEnabled,
                            })}
                        />
                    </Field>
                </Section>
                {advancedView && (
                    <Section title='Branding'>
                        <Field label='Hide cielo24 logo' disabled={values.onlySeo}>
                            <Toggle {...this.getFieldProps('hideCielo24Logo')} />
                        </Field>
                        <Field label='Disable cielo24 link' disabled={values.onlySeo}>
                            <Toggle {...this.getFieldProps('disableCielo24Link')} />
                        </Field>
                        <Field label='Client logo image' disabled={values.onlySeo}>
                            <TextInput
                                grow={true}
                                placeholder='Client logo image'
                                {...this.getFieldProps('clientLogoImage')}
                            />
                        </Field>
                        <Field label='Client logo link' disabled={values.onlySeo}>
                            <TextInput
                                grow={true}
                                placeholder='Client logo link'
                                {...this.getFieldProps('clientLogoHref')}
                            />
                        </Field>
                        <Field label='Sponsor logo image' disabled={values.onlySeo}>
                            <TextInput
                                grow={true}
                                placeholder='Sponsor logo image'
                                {...this.getFieldProps('sponsorLogoImage')}
                            />
                        </Field>
                        <Field label='Sponsor logo link' disabled={values.onlySeo}>
                            <TextInput
                                grow={true}
                                placeholder='Sponsor logo link'
                                {...this.getFieldProps('sponsorLogoHref')}
                            />
                        </Field>
                    </Section>
                )}
                {advancedView &&
                    <Section title='Preview'>
                        <Field label='Display mock content'>
                            <Toggle {...this.getFieldProps('displayMockContent')} />
                        </Field>
                        <Field label='Body background'>
                            <Select
                                options={options.bodyBackgroundOptions}
                                {...this.getFieldProps('bodyBackground')}
                            />
                        </Field>
                        {!mobile && (
                            <Field
                                label='Container width'
                                disabled={!values.domContainerEnabled || values.onlySeo || mobile}
                            >
                                <Select
                                    options={options.containerWidthOptions}
                                    {...this.getFieldProps('containerWidth', {
                                        disabled: !values.domContainerEnabled || values.onlySeo || mobile,
                                    })}
                                />
                            </Field>
                        )}
                        {!mobile && (
                            <Field
                                label='Container height'
                                disabled={!values.domContainerEnabled || values.onlySeo || mobile}
                            >
                                <Select
                                    options={options.containerHeightOptions}
                                    {...this.getFieldProps('containerHeight', {
                                        disabled: !values.domContainerEnabled || values.onlySeo || mobile,
                                    })}
                                />
                            </Field>
                        )}
                    </Section>
                }
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./scripts/test-page/form.js